import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Button,
  Divider,
  Stack,
  Container,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  TextField,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import {
  AccessTime as TimeIcon,
  CameraAlt as CameraIcon,
  AddToPhotos as OTIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  HourglassTop as PendingIcon,
  CheckCircle as ApprovedIcon,
  LocationOff as LocationOffIcon,
} from '@mui/icons-material';
import * as actions from '../../redux/actions';
import { OT_STATUS } from 'utils/constants';
import MainTimeStamp from './TimeStampMain';
import CheckoutGpsBlockDialog from '../Dialogs/CheckoutGpsBlockDialog';
import useCheckoutGpsGuard from '../../hooks/useCheckoutGpsGuard';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

// การ์ดสรุปงานประจำวัน — พนักงานเขียนว่าวันนี้ทำอะไรบ้าง ผูกกับ timestamp รายการที่แสดงอยู่
const SUMMARY_MAX = 2000;
const SUMMARY_MAX_FILES = 10;
const SUMMARY_MAX_MB = 10;
const isImageUrl = (url) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url || '');
function DailySummaryCard({ timestamp }) {
  const saved = timestamp?.daily_summary?.text || '';
  const savedFiles = timestamp?.daily_summary?.attachments || [];
  const [text, setText] = useState(saved);
  const [files, setFiles] = useState(savedFiles);
  const [lastSaved, setLastSaved] = useState(JSON.stringify({ text: saved, files: savedFiles }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = picked.filter((f) => f.size > SUMMARY_MAX_MB * 1024 * 1024);
    if (tooBig.length) {
      showAlert(`ไฟล์ใหญ่เกิน ${SUMMARY_MAX_MB} MB: ${tooBig.map((f) => f.name).join(', ')}`);
    }
    const ok = picked
      .filter((f) => f.size <= SUMMARY_MAX_MB * 1024 * 1024)
      .slice(0, SUMMARY_MAX_FILES - files.length);
    if (!ok.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(ok.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/timestamp/summary-attachment`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return { name: file.name, url: data.url };
      }));
      setFiles((cur) => [...cur, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(
        `${process.env.REACT_APP_API_URL}/timestamp/${timestamp._id}/summary`,
        { text, attachments: files },
      );
      setLastSaved(JSON.stringify({ text: text.trim(), files }));
      showAlert('บันทึกสรุปงานแล้ว');
    } finally {
      setSaving(false);
    }
  };

  const savedState = JSON.parse(lastSaved);
  const hasSaved = !!savedState.text || savedState.files.length > 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography fontWeight={600}>สรุปงานวันนี้</Typography>
        {hasSaved && (
          <Chip size="small" color="success" variant="outlined" label="บันทึกแล้ว" />
        )}
      </Stack>
      <TextField
        multiline
        minRows={3}
        fullWidth
        placeholder="วันนี้ทำอะไรบ้าง เช่น งานที่ทำ ความคืบหน้า ปัญหาที่เจอ"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, SUMMARY_MAX))}
        helperText={`${text.length}/${SUMMARY_MAX}`}
      />
      {files.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {files.map((f) => (
            <Box key={f.url} sx={{ position: 'relative' }}>
              {isImageUrl(f.url) ? (
                <a href={f.url} target="_blank" rel="noreferrer">
                  <img
                    src={f.url}
                    alt={f.name}
                    style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block' }}
                  />
                </a>
              ) : (
                <Chip
                  component="a"
                  href={f.url}
                  target="_blank"
                  clickable
                  label={f.name || 'ไฟล์แนบ'}
                  sx={{ maxWidth: 200 }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => setFiles((cur) => cur.filter((x) => x.url !== f.url))}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #e5e7eb', p: 0.25, '&:hover': { bgcolor: '#fee2e2' } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        <Button
          component="label"
          size="small"
          startIcon={<AttachFileIcon />}
          disabled={uploading || files.length >= SUMMARY_MAX_FILES}
        >
          {uploading ? 'กำลังอัปโหลด...' : 'แนบรูป/ไฟล์'}
          <input hidden type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handlePickFiles} />
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || uploading || JSON.stringify({ text: text.trim(), files }) === lastSaved}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึกสรุปงาน'}
        </Button>
      </Stack>
    </Card>
  );
}

export default function ListTimestamp({ timestamp, setOpen }) {
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString('th'),
  );
  const [imageStatus, setImageStatus] = useState('loading'); // 'loading', 'success', 'error', 'none'
  const history = useHistory();
  const dispatch = useDispatch();
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const profile = me?.userData;
  const {
    checkGpsForCheckout,
    gpsBlockOpen,
    setGpsBlockOpen,
    gpsBlockReason,
  } = useCheckoutGpsGuard(timestamp, me, info);

  const handleCheckoutClick = () => {
    // เช็ค GPS ก่อนพาไปหน้า checkout เลย — ถ้าติด block ให้ dialog ขึ้นตรงนี้
    // ไม่ route ไปหน้า /profile/timestamp/Check-out/:id เลย
    if (!checkGpsForCheckout()) return;
    history.push(`/profile/timestamp/Check-out/${timestamp?._id}`);
  };

  useEffect(() => {
    dispatch(actions.getInformation());
    return () => {};
  }, []); // eslint-disable-line

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('th'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // ตรวจสอบสถานะรูปภาพ
    if (!timestamp?.image?.url) {
      setImageStatus('none');
    } else {
      setImageStatus('loading');
      // ลองโหลดรูปเพื่อตรวจสอบว่าใช้งานได้
      const img = new Image();
      img.onload = () => setImageStatus('success');
      img.onerror = () => setImageStatus('error');
      img.src = timestamp.image.url;
    }
  }, [timestamp?.image?.url]);

  if (!timestamp?._id) return <MainTimeStamp />;

  // otRequests อาจเป็น array หรือ object เดียว
  const otRequests = Array.isArray(timestamp.otRequests)
    ? timestamp.otRequests
    : timestamp.otRequest
    ? [timestamp.otRequest]
    : [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)',
        }}
      >
        {/* Time Header */}
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #FF7300FF 0%, #FFA600FF 100%)',
            color: 'white',
            borderRadius: '16px 16px 0 0',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={profile?.image?.url}
                sx={{ width: 56, height: 56, border: '2px solid white' }}
              />
              <Box>
                <Typography variant="h6">
                  {formatEmployeeThaiName(profile)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {profile?.department?.name}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TimeIcon />
              <Typography variant="h4" sx={{ fontFamily: 'monospace' }}>
                {currentTime}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          <Stack spacing={1}>
            {/* Status Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.75,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: timestamp?.late_status ? '#ffe0b2' : '#e2e8f0',
                bgcolor: '#fff',
              }}
            >
              {/* แถว 1: เวลา + วันที่ + ป้ายสถานะ | ป้ายประเภทพนักงาน */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: timestamp?.late_status ? '#fff3e0' : '#ecfdf5',
                      color: timestamp?.late_status ? 'warning.main' : 'success.main',
                      flexShrink: 0,
                    }}
                  >
                    <TimeIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography fontWeight={700} fontSize="1rem" color="#0f172a" sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs(timestamp?.checkIn).format('HH:mm น.')}
                  </Typography>
                  <Typography variant="caption" color="#94a3b8" sx={{ whiteSpace: 'nowrap' }}>
                    {dayjs(timestamp?.checkIn).format('DD/MM/YYYY')}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                      bgcolor: timestamp?.late_status ? '#fff3e0' : '#ecfdf5',
                      color: timestamp?.late_status ? '#e65100' : '#047857',
                    }}
                  >
                    {timestamp?.late_status ? 'เข้างานสาย' : 'เข้างาน'}
                  </Box>
                </Stack>
                {timestamp?.employeeType && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: '#f1f5f9',
                      color: '#475569',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {timestamp.employeeType === 'FULLTIME' ? 'รายเดือน' : 'รายวัน'}
                  </Box>
                )}
              </Stack>

              {/* แถว 2: โครงการและชื่องาน */}
              {timestamp?.project_in?.name && (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ fontSize: '0.75rem', color: '#475569', minWidth: 0 }}
                  title={timestamp.project_in.name}
                >
                  <Box component="span" sx={{ color: '#94a3b8', mr: 0.75, flexShrink: 0 }}>
                    โครงการ:
                  </Box>
                  {timestamp.project_in.project_number && (
                    <>
                      <Box component="span" sx={{ fontWeight: 600, color: '#334155', mr: 0.5, flexShrink: 0 }}>
                        {timestamp.project_in.project_number}
                      </Box>
                      <Box component="span" sx={{ color: '#cbd5e1', mx: 0.5 }}>·</Box>
                    </>
                  )}
                  <Box component="span" noWrap sx={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {timestamp.project_in.name}
                  </Box>
                </Stack>
              )}

              {/* แถว 3 (แสดงเฉพาะเมื่อมี): นอกพื้นที่ / หมายเหตุ */}
              {(timestamp?.out_of_location || timestamp?.note) && (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ fontSize: '0.75rem' }}>
                  {timestamp?.out_of_location && (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<LocationOffIcon />}
                      color="warning"
                      label={
                        timestamp?.locationCheckIn?.nearestProject?.distanceText
                          ? `นอกพื้นที่ · ห่าง ${timestamp.locationCheckIn.nearestProject.distanceText}`
                          : 'นอกพื้นที่'
                      }
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {timestamp?.note && (
                    <Box component="span" sx={{ color: '#64748b' }}>
                      <Box component="span" sx={{ color: '#94a3b8', mr: 0.5 }}>หมายเหตุ:</Box>
                      {timestamp.note}
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            {/* Out-of-location approval status */}
            {timestamp?.approval_status === 'PENDING' && (
              <Alert
                severity="warning"
                icon={<PendingIcon />}
                sx={{ borderRadius: 2 }}
              >
                <AlertTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOffIcon fontSize="small" />
                  คำขอลงเวลานอกพื้นที่ — รอ HR อนุมัติ
                </AlertTitle>
                การลงเวลานี้จะถูกนับเมื่อ HR อนุมัติแล้วเท่านั้น
              </Alert>
            )}
            {timestamp?.approval_status === 'APPROVED' && (
              <Alert
                severity="success"
                icon={<ApprovedIcon />}
                sx={{ borderRadius: 2 }}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>
                  HR อนุมัติแล้ว — นับเป็นการลงเวลา
                </AlertTitle>
                {timestamp?.approved_by && (
                  <Typography variant="caption" display="block">
                    อนุมัติโดย: <strong>{timestamp.approved_by}</strong>
                    {timestamp?.approved_at
                      ? ` · ${dayjs(timestamp.approved_at).format('DD/MM/YY HH:mm')}`
                      : ''}
                  </Typography>
                )}
              </Alert>
            )}

            {/* OT Status */}
            {timestamp?.ot_status?.require && (
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography>สถานะ OT</Typography>
                  <Chip
                    label={
                      OT_STATUS?.[timestamp?.ot_status?.status]?.status_code
                    }
                    color={OT_STATUS?.[timestamp?.ot_status?.status]?.color}
                    variant="outlined"
                  />
                </Stack>
              </Card>
            )}

            {/* OT Request Info */}
            {otRequests.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#f3f6fa',
                  borderColor: '#ffa600',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  รายการขอ OT
                </Typography>
                <Stack spacing={1}>
                  {otRequests.map((ot, idx) => (
                    <Box key={ot._id || idx} sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={
                            OT_STATUS?.[ot.status]?.status_code || ot.status
                          }
                          color={OT_STATUS?.[ot.status]?.color || 'default'}
                          size="small"
                          variant="filled"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {ot?.description || '-'}
                        </Typography>
                      </Stack>
                      <Typography variant="caption">
                        วันที่ขอ:{' '}
                        {ot?.date ? dayjs(ot.date).format('DD/MM/YYYY') : '-'}
                        {ot?.startTime && ot?.endTime
                          ? ` (${dayjs(ot.startTime).format('HH:mm')} - ${dayjs(
                              ot.endTime,
                            ).format('HH:mm')})`
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Image Status Alert */}
            {imageStatus === 'none' && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ borderRadius: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>ไม่มีรูปภาพเช็คอิน</AlertTitle>
                ไม่พบรูปภาพการเช็คอินของคุณ กรุณาตรวจสอบให้แน่ใจว่าได้ถ่ายรูปเช็คอินทุกครั้ง
              </Alert>
            )}
            {imageStatus === 'error' && (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ borderRadius: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>ไม่สามารถโหลดรูปภาพได้</AlertTitle>
                รูปภาพเช็คอินของคุณอาจเสียหายหรือไม่สามารถเข้าถึงได้ กรุณาติดต่อแอดมิน
              </Alert>
            )}
            {imageStatus === 'loading' && (
              <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ borderRadius: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>กำลังโหลดรูปภาพ</AlertTitle>
                กรุณารอสักครู่...
              </Alert>
            )}

            {/* Check-in Photo */}
            {imageStatus === 'success' && timestamp?.image?.url && (
              <Card
                variant="outlined"
                sx={{ borderRadius: 2, overflow: 'hidden' }}
              >
                <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CameraIcon color="primary" />
                    <Typography>ภาพถ่ายเช็คอิน</Typography>
                  </Stack>
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                  <img
                    src={timestamp?.image?.url}
                    alt="Check-in"
                    style={{
                      width: '100%',
                      borderRadius: 8,
                      maxHeight: 300,
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </Card>
            )}

            {/* สรุปงานประจำวัน */}
            <DailySummaryCard key={timestamp._id} timestamp={timestamp} />

            {/* Action Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<OTIcon />}
                onClick={() => history.push('/profile/ot', { openAdd: true })}
                disabled={otRequests.length > 0 || timestamp?.approval_status === 'PENDING'}
                sx={{ py: 1.5 }}
              >
                ขอ OT
              </Button>

              <Button
                fullWidth
                variant="contained"
                color="error"
                size="large"
                startIcon={<LogoutIcon />}
                onClick={handleCheckoutClick}
                disabled={timestamp?.approval_status === 'PENDING'}
                sx={{ py: 1.5 }}
              >
                {timestamp?.approval_status === 'PENDING' ? 'รอ HR อนุมัติ...' : 'ลงเวลาออกงาน'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Card>

      <CheckoutGpsBlockDialog
        open={gpsBlockOpen}
        reason={gpsBlockReason}
        onClose={() => setGpsBlockOpen(false)}
      />
    </Container>
  );
}
